// Dashboard - Light Professional Theme
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase, supabaseUrl } from '../lib/supabase';
import { roadmapData } from '../roadmapData';
import useUserConfig from '../hooks/useUserConfig';
import progressSyncService from '../services/ProgressSyncService';
import './Dashboard.css';

// Calculate real stats from roadmapData - with Supabase support
const calculateFixarrStats = async (userId) => {
    let totalTasks = 0;
    let completedTasks = 0;
    let totalPhases = roadmapData.length;

    let savedTasks = [];
    let checklistData = {};

    // Try to load from Supabase if user is logged in
    if (userId) {
        try {
            const remoteData = await progressSyncService.loadProgress(userId);
            if (remoteData) {
                savedTasks = remoteData.taskStates;
                checklistData = remoteData.checklistStates;
            }
        } catch (err) {
            console.error('Failed to load from Supabase, using localStorage:', err);
        }
    }

    // Fallback to localStorage if no remote data
    if (savedTasks.length === 0) {
        const savedState = localStorage.getItem('fixarr-roadmap-state-v6');
        savedTasks = savedState ? JSON.parse(savedState) : [];

        const savedChecklist = localStorage.getItem('fixarr-roadmap-checklist-v1');
        checklistData = savedChecklist ? JSON.parse(savedChecklist) : {};
    }

    roadmapData.forEach(phase => {
        phase.tasks.forEach(task => {
            totalTasks++;
            const savedTask = savedTasks.find(t => t.id === task.id);
            const status = savedTask ? savedTask.status : task.status;

            // Check if task has checklist - same logic as RoadmapDinamico
            if (task.checklist && task.checklist.length > 0) {
                const taskChecklist = checklistData[task.id] || {};
                const completedItems = task.checklist.filter(item => taskChecklist[item.id]).length;

                if (completedItems === task.checklist.length) {
                    completedTasks++;
                }
            } else {
                // No checklist - use status
                if (status === 'completed') {
                    completedTasks++;
                }
            }
        });
    });

    const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    return { progress, totalTasks, totalPhases, completedTasks };
};

const Dashboard = () => {
    const { user, signOut } = useAuth();
    const navigate = useNavigate();
    const { config, isMinimumSetupDone, isCreator } = useUserConfig(user);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSetupBanner, setShowSetupBanner] = useState(true);

    useEffect(() => {
        loadProjects();
    }, [user]);

    // Redirigir a Stripe Checkout si el usuario acaba de registrarse con un plan
    useEffect(() => {
        if (!user) return;
        const pending = sessionStorage.getItem('roadmap_pending_checkout');
        if (!pending) return;
        let cancelled = false;
        (async () => {
            try {
                const { plan, billing } = JSON.parse(pending);
                sessionStorage.removeItem('roadmap_pending_checkout');
                const { data: { session } } = await supabase.auth.getSession();
                if (!session?.access_token || cancelled) return;
                const res = await fetch(`${supabaseUrl}/functions/v1/roadmap-stripe-checkout`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify({
                        plan,
                        billing,
                        userId: user.id,
                        userEmail: user.email,
                    }),
                });
                const data = await res.json();
                if (data.url && !cancelled) window.location.href = data.url;
                else if (data.error) console.warn('Checkout:', data.error);
            } catch (e) {
                console.warn('Checkout redirect failed:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [user]);

    const loadProjects = async () => {
        if (!user) return;

        try {
            const { data, error } = await supabase
                .from('projects')
                .select('*')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false });

            if (error) throw error;

            // Calculate real FIXARR stats (async - loads from Supabase)
            const fixarrStats = await calculateFixarrStats(user.id);

            // Always add FIXARR as the main project with real data
            const fixarrProject = {
                id: 'fixarr',
                name: 'FIXARR',
                description: 'Sistema de Gestión de Servicios Técnicos',
                progress: fixarrStats.progress,
                tasks_count: fixarrStats.totalTasks,
                phases_count: fixarrStats.totalPhases,
                completed_count: fixarrStats.completedTasks
            };

            setProjects([fixarrProject, ...(data || [])]);
        } catch (err) {
            console.error('Error loading projects:', err);
            // Show FIXARR project with real data even on error
            const fixarrStats = await calculateFixarrStats(user.id);
            setProjects([{
                id: 'fixarr',
                name: 'FIXARR',
                description: 'Sistema de Gestión de Servicios Técnicos',
                progress: fixarrStats.progress,
                tasks_count: fixarrStats.totalTasks,
                phases_count: fixarrStats.totalPhases,
                completed_count: fixarrStats.completedTasks
            }]);
        } finally {
            setLoading(false);
        }
    };

    const handleSignOut = async () => {
        await signOut();
        navigate('/');
    };

    const filteredProjects = projects.filter(project =>
        project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getUserInitial = () => {
        return user?.email?.charAt(0).toUpperCase() || 'U';
    };

    const getUserAvatar = () => {
        return user?.user_metadata?.avatar_url || null;
    };

    const getUserName = () => {
        return user?.user_metadata?.user_name || user?.email?.split('@')[0] || 'Usuario';
    };

    return (
        <div className="dashboard-container">
            <header className="dashboard-header">
                <Link to="/" className="dashboard-logo">
                    <div className="dashboard-logo-icon">⚡</div>
                    <span className="dashboard-logo-text">Roadmap<span>Platform</span></span>
                </Link>

                <div className="dashboard-user">
                    {/* Settings Button */}
                    <Link to="/settings" className="dashboard-settings-btn" title="Configuración">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="3" />
                            <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                        </svg>
                        {!config.setupCompleted && <span className="settings-badge">!</span>}
                    </Link>

                    <div className="dashboard-user-info">
                        {getUserAvatar() ? (
                            <img src={getUserAvatar()} alt="Avatar" className="dashboard-user-avatar-img" />
                        ) : (
                            <div className="dashboard-user-avatar">
                                {getUserInitial()}
                            </div>
                        )}
                        <span className="dashboard-user-name">@{getUserName()}</span>
                    </div>
                    <button className="dashboard-logout-btn" onClick={handleSignOut}>
                        Cerrar Sesión
                    </button>
                </div>
            </header>

            <main className="dashboard-main">
                {/* Setup Banner - Show if not configured */}
                {!config.setupCompleted && showSetupBanner && (
                    <div className="dashboard-setup-banner">
                        <div className="setup-banner-content">
                            <div className="setup-banner-icon">⚙️</div>
                            <div className="setup-banner-text">
                                <strong>Configura tu espacio de trabajo</strong>
                                <p>Conecta tu Supabase y GitHub para crear proyectos y hacer backups automáticos.</p>
                            </div>
                        </div>
                        <div className="setup-banner-actions">
                            <Link to="/settings" className="setup-banner-btn">
                                Configurar ahora →
                            </Link>
                            <button
                                className="setup-banner-dismiss"
                                onClick={() => setShowSetupBanner(false)}
                            >
                                Más tarde
                            </button>
                        </div>
                    </div>
                )}

                <div className="dashboard-welcome">
                    <h1>Mis Proyectos</h1>
                    <p>Gestiona todos tus roadmaps en un solo lugar</p>
                </div>

                <div className="dashboard-actions">
                    <div className="dashboard-search">
                        <span>🔍</span>
                        <input
                            type="text"
                            placeholder="Buscar proyectos..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <Link to="/new-project" className="dashboard-new-btn">
                        <span>➕</span>
                        Nuevo Proyecto
                    </Link>
                </div>

                {loading ? (
                    <div className="dashboard-loading">
                        <div className="dashboard-spinner"></div>
                        <p>Cargando proyectos...</p>
                    </div>
                ) : filteredProjects.length === 0 && !searchQuery ? (
                    <div className="dashboard-empty">
                        <div className="dashboard-empty-icon">📁</div>
                        <h3>Aún no tienes proyectos</h3>
                        <p>Crea tu primer proyecto y empieza a gestionar tu roadmap</p>
                        <Link to="/new-project" className="dashboard-new-btn">
                            <span>➕</span>
                            Crear mi primer proyecto
                        </Link>
                    </div>
                ) : (
                    <div className="dashboard-projects-grid">
                        {filteredProjects.map(project => (
                            <div key={project.id} className="dashboard-project-card">
                                <Link to={`/project/${project.id}`} className="project-card-link">
                                    <div className="dashboard-project-header">
                                        <div className="dashboard-project-icon">
                                            {project.name === 'FIXARR' ? '⚡' : '📦'}
                                        </div>
                                        <span className={`dashboard-project-status ${project.progress < 100 ? 'in-progress' : ''}`}>
                                            {project.progress >= 100 ? 'Completado' : 'En progreso'}
                                        </span>
                                    </div>

                                    <h3>{project.name}</h3>
                                    <p>{project.description}</p>

                                    <div className="dashboard-project-progress">
                                        <div className="dashboard-progress-bar">
                                            <div
                                                className="dashboard-progress-fill"
                                                style={{ width: `${project.progress || 0}%` }}
                                            ></div>
                                        </div>
                                        <div className="dashboard-progress-text">
                                            <span>{project.progress || 0}% completado</span>
                                        </div>
                                    </div>

                                    <div className="dashboard-project-meta">
                                        <div className="dashboard-project-meta-item">
                                            📋 {project.phases_count || 0} fases
                                        </div>
                                        <div className="dashboard-project-meta-item">
                                            ✓ {project.completed_count || 0}/{project.tasks_count || 0} tareas
                                        </div>
                                    </div>
                                </Link>

                                {/* Quick Actions */}
                                <div className="project-quick-actions">
                                    <Link
                                        to={`/project/${project.id}`}
                                        className="quick-action-btn primary"
                                        title="Ver Roadmap"
                                    >
                                        <span>📋</span> Roadmap
                                    </Link>
                                    <Link
                                        to={`/project/${project.id}?dictate=true`}
                                        className="quick-action-btn voice"
                                        title="Dictar una idea"
                                    >
                                        <span>🎤</span> Dictar idea
                                    </Link>
                                    {project.id !== 'fixarr' && (
                                        <Link
                                            to={`/project/${project.id}/share`}
                                            className="quick-action-btn share"
                                            title="Compartir proyecto"
                                        >
                                            <span>👥</span> Compartir
                                        </Link>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default Dashboard;
